/* eslint-disable no-console */
'use client';

import { useEffect, useMemo } from 'react';

const loadedFonts = new Set();
const loadedTypekitKits = new Set();

/**
 * Adobe Fonts / Platform Fonts Registry
 * Maps font identifiers to their CSS font-family values and required Typekit kit IDs
 */
export const ADOBE_FONTS_REGISTRY = {
  // Owners family (kit: bmu5bue) - from OWN Typekit project
  owners: {
    fontFamily: "'owners', sans-serif",
    kitId: 'bmu5bue',
    label: 'Owners',
    weights: [400, 700],
    styles: ['normal', 'italic'],
  },
  'owners-wide': {
    fontFamily: "'owners-wide', sans-serif",
    kitId: 'wng0qte',
    label: 'Owners Wide',
    weights: [400, 500, 700],
    styles: ['normal'],
  },
  'owners-text': {
    fontFamily: "'owners-text', sans-serif",
    kitId: 'wng0qte',
    label: 'Owners Text',
    weights: [400, 500, 700],
    styles: ['normal', 'italic'],
  },
  'owners-narrow': {
    fontFamily: "'owners-narrow', sans-serif",
    kitId: 'wng0qte',
    label: 'Owners Narrow',
    weights: [400, 500, 700],
    styles: ['normal'],
  },
  // Platform default fonts (no kit needed, already loaded globally)
  'ibm-plex-sans': {
    fontFamily: "'IBM Plex Sans', sans-serif",
    kitId: null, // Already loaded via Google Fonts
    label: 'IBM Plex Sans',
    weights: [400, 500, 700],
    styles: ['normal', 'italic'],
  },
};

/**
 * Dynamically loads a Typekit kit if not already loaded
 * @param {string} kitId - The Adobe Fonts kit ID
 */
const loadTypekitKit = (kitId) => {
  if (!kitId || loadedTypekitKits.has(kitId)) return;

  // Check if the kit CSS is already in the document
  const existingLink = document.querySelector(`link[href*="${kitId}"]`);
  if (existingLink) {
    loadedTypekitKits.add(kitId);
    return;
  }

  // Add the CSS link for the kit
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = `https://use.typekit.net/${kitId}.css`;
  document.head.appendChild(link);

  if (process.env.NODE_ENV === 'development') {
    console.log('[useCustomFont] Loaded Typekit kit:', kitId);
  }

  loadedTypekitKits.add(kitId);
};

const useCustomFont = ({
  fontSource = 'default',
  googleFontName = '',
  googleFontCustom = '',
  googleFontWeights = '400;500;700',
  customFontUrl = '',
  customFontFamily = '',
  adobeFontName = '',
}) => {
  const resolvedFontUrl = useMemo(() => {
    if (!customFontUrl) return '';
    if (typeof customFontUrl === 'string') return customFontUrl;
    if (typeof customFontUrl === 'object' && customFontUrl.url) return customFontUrl.url;
    if (typeof customFontUrl === 'object' && customFontUrl.src) return customFontUrl.src;
    return '';
  }, [customFontUrl]);

  const effectiveGoogleFontName = useMemo(() => {
    if (googleFontName === 'custom' && googleFontCustom) {
      return googleFontCustom;
    }
    return googleFontName;
  }, [googleFontName, googleFontCustom]);

  const fontFamily = useMemo(() => {
    const defaultFont = "'IBM Plex Sans', sans-serif";

    if (fontSource === 'googleFonts') {
      if (!effectiveGoogleFontName || effectiveGoogleFontName === 'custom') {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useCustomFont] Google Fonts selected but no font name provided, using default',
          );
        }
        return defaultFont;
      }
      return `'${effectiveGoogleFontName}', sans-serif`;
    }

    if (fontSource === 'customUpload') {
      if (!customFontFamily || !resolvedFontUrl) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useCustomFont] Custom upload selected but missing font family or URL, using default',
          );
        }
        return defaultFont;
      }
      return `'${customFontFamily}', sans-serif`;
    }

    if (fontSource === 'adobeFonts') {
      if (!adobeFontName) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[useCustomFont] Adobe Fonts selected but no font name provided, using default',
          );
        }
        return defaultFont;
      }
      const fontConfig = ADOBE_FONTS_REGISTRY[adobeFontName];
      if (fontConfig) {
        return fontConfig.fontFamily;
      }
      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `[useCustomFont] Adobe font "${adobeFontName}" not found in registry, using default`,
        );
      }
      return defaultFont;
    }

    return defaultFont;
  }, [fontSource, effectiveGoogleFontName, customFontFamily, adobeFontName, resolvedFontUrl]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (process.env.NODE_ENV === 'development') {
      console.log('[useCustomFont] fontSource:', fontSource);
      console.log('[useCustomFont] adobeFontName:', adobeFontName);
      console.log('[useCustomFont] customFontUrl:', customFontUrl);
      console.log('[useCustomFont] resolvedFontUrl:', resolvedFontUrl);
      console.log('[useCustomFont] customFontFamily:', customFontFamily);
    }

    // Handle Adobe Fonts / Platform Fonts
    if (fontSource === 'adobeFonts' && adobeFontName) {
      const fontConfig = ADOBE_FONTS_REGISTRY[adobeFontName];
      if (fontConfig && fontConfig.kitId) {
        loadTypekitKit(fontConfig.kitId);
      }
    }

    if (
      fontSource === 'googleFonts' &&
      effectiveGoogleFontName &&
      effectiveGoogleFontName !== 'custom'
    ) {
      const fontKey = `google-${effectiveGoogleFontName}-${googleFontWeights}`;
      if (loadedFonts.has(fontKey)) return;

      const weights = googleFontWeights.replace(/;/g, ',');
      const fontUrl = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(effectiveGoogleFontName)}:wght@${weights}&display=swap`;

      const existingLink = document.querySelector(`link[href="${fontUrl}"]`);
      if (!existingLink) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontUrl;
        document.head.appendChild(link);
      }
      loadedFonts.add(fontKey);
    }

    if (fontSource === 'customUpload' && resolvedFontUrl && customFontFamily) {
      const fontKey = `custom-${customFontFamily}`;
      if (loadedFonts.has(fontKey)) return;

      const existingStyle = document.querySelector(`style[data-font="${customFontFamily}"]`);
      if (!existingStyle) {
        const style = document.createElement('style');
        style.setAttribute('data-font', customFontFamily);
        style.textContent = `
          @font-face {
            font-family: '${customFontFamily}';
            src: url('${resolvedFontUrl}') format('woff2'),
                 url('${resolvedFontUrl}') format('woff'),
                 url('${resolvedFontUrl}') format('truetype');
            font-weight: normal;
            font-style: normal;
            font-display: swap;
          }
        `;
        document.head.appendChild(style);
        if (process.env.NODE_ENV === 'development') {
          console.log('[useCustomFont] @font-face created for:', customFontFamily);
        }
      }
      loadedFonts.add(fontKey);
    }
  }, [
    fontSource,
    effectiveGoogleFontName,
    googleFontWeights,
    customFontUrl,
    resolvedFontUrl,
    customFontFamily,
    adobeFontName,
  ]);

  return { fontFamily };
};

export default useCustomFont;
